import React from "react";
import { TextField, ITextField, Callout, DirectionalHint, ActionButton, Label, Stack, getId } from "@fluentui/react";

export interface IAutocompleteProps {
  id?: string;
  className?: string;
  name?: string;
  value?: string;
  required?: boolean;
  errorMessage?: string;
  label?: string;
  onChange?: (newValue?: string) => void;
  onBlur?: () => void;
  onSuggest?: (value?: string) => string[];
  disabled?: boolean;
  transformValue?: (newValue?: string) => string;
}

interface IAutocompleteState {
  errorMessage?: string;
  value?: string;
  isFocused: boolean;
  isCalloutHover: boolean;
  hideSuggestions: boolean;
  suggestions: string[];
  highlightedIndex: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export class Autocomplete extends React.Component<IAutocompleteProps, IAutocompleteState> {
  private readonly textFieldWrapperRef = React.createRef<HTMLDivElement>();
  public ref = React.createRef<ITextField>();
  private readonly textFieldId = getId("autocomplete-");
  private readonly listboxId = getId("autocomplete-listbox-");
  private timeout: number | undefined;

  public state: IAutocompleteState = {
    errorMessage: this.props.errorMessage,
    value: this.props.value,
    isFocused: false,
    isCalloutHover: false,
    hideSuggestions: false,
    suggestions: [],
    highlightedIndex: -1
  };

  public render(): React.ReactNode {
    // const { id, className = "", name, required, errorMessage, label, disabled = false } = this.props;
    const { id, className = "", name, required, label, disabled = false } = this.props;
    const suggestionItems = this.renderSuggestionItems(this.state.suggestions);
    const calloutWidth = this.textFieldWrapperRef.current ? this.textFieldWrapperRef.current.offsetWidth : undefined;
    const calloutHidden =
      this.state.hideSuggestions ||
      this.state.suggestions.length === 0 ||
      (!this.state.isFocused && !this.state.isCalloutHover);
    const isExpanded = !calloutHidden;
    const activeDescendant =
      isExpanded && this.state.highlightedIndex >= 0
        ? `${this.listboxId}-option-${this.state.highlightedIndex}`
        : undefined;

    return (
      <React.Fragment>
        <Stack disableShrink>
          <Label htmlFor={this.textFieldId} required={required}>
            {label}
          </Label>
          <div id={id} className={`autocomplete ${className}`} ref={this.textFieldWrapperRef}>
            <TextField
              componentRef={this.ref}
              id={this.textFieldId}
              role="combobox"
              ariaLabel={label}
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? this.listboxId : undefined}
              aria-activedescendant={activeDescendant}
              aria-autocomplete="list"
              name={name}
              value={this.state.value}
              autoComplete="off"
              errorMessage={this.state.errorMessage}
              disabled={disabled}
              required={required}
              onChange={disabled ? noop : this.onChange}
              onFocus={disabled ? noop : this.onFocus}
              onBlur={disabled ? noop : this.onBlur}
              onKeyDown={disabled ? noop : this.onKeyDown}
            />
          </div>
          <Callout
            onMouseEnter={this.onCalloutMouseEnter}
            onMouseLeave={this.onCalloutMouseLeave}
            isBeakVisible={false}
            hidden={calloutHidden}
            target={this.textFieldWrapperRef.current}
            directionalHint={DirectionalHint.bottomLeftEdge}
            style={{ minWidth: calloutWidth }}
          >
            <div id={this.listboxId} role="listbox" aria-label={`${label} suggestions`}>
              {suggestionItems}
            </div>
          </Callout>
        </Stack>
      </React.Fragment>
    );
  }

  private transformValue(newValue?: string): string {
    if (this.props.transformValue) return this.props.transformValue(newValue);
    return newValue || "";
  }

  public componentDidMount(): void {
    this.updateSuggestions();
  }

  public componentDidUpdate(oldProps: IAutocompleteProps, oldState: IAutocompleteState): void {
    if (oldProps.value !== this.props.value || oldProps.errorMessage !== this.props.errorMessage) {
      this.setState({ value: this.transformValue(this.props.value), errorMessage: this.props.errorMessage });
    }

    if (oldState.value !== this.state.value) {
      this.updateSuggestions();
    }
  }

  public componentWillUnmount(): void {
    window.clearTimeout(this.timeout);
  }

  private updateSuggestions() {
    const { onSuggest = () => [] } = this.props;

    window.clearTimeout(this.timeout);

    this.timeout = window.setTimeout(() => {
      this.timeout = undefined;
      this.setState(({ value }) => ({ suggestions: onSuggest(value), highlightedIndex: -1 }));
    }, 100);
  }

  private readonly onChange = (_event: any, value = ""): void => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const { onChange = () => {} } = this.props;

    this.setState({
      value: this.transformValue(this.props.value),
      errorMessage: this.props.errorMessage,
      hideSuggestions: false
    });

    onChange(value);
  };

  private readonly onFocus = () => this.setState({ isFocused: true, hideSuggestions: false });

  private readonly onBlur = () => this.setState({ isFocused: false });

  private readonly onCalloutMouseEnter = () => this.setState({ isCalloutHover: true });

  private readonly onCalloutMouseLeave = () => this.setState({ isCalloutHover: false });

  private readonly onKeyDown = (event: React.KeyboardEvent): void => {
    const { suggestions, highlightedIndex } = this.state;
    const isOpen =
      !this.state.hideSuggestions && suggestions.length > 0 && (this.state.isFocused || this.state.isCalloutHover);

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (isOpen) {
          this.setState({
            highlightedIndex: highlightedIndex < suggestions.length - 1 ? highlightedIndex + 1 : 0
          });
        } else {
          this.setState({ hideSuggestions: false });
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) {
          this.setState({
            highlightedIndex: highlightedIndex > 0 ? highlightedIndex - 1 : suggestions.length - 1
          });
        }
        break;
      case "Enter":
        if (!event.ctrlKey && isOpen) {
          event.preventDefault();
          const index = highlightedIndex >= 0 ? highlightedIndex : 0;
          if (suggestions[index]) {
            this.selectSuggestion(suggestions[index]);
          }
        }
        break;
      case "Escape":
        if (isOpen) {
          event.preventDefault();
          this.setState({ hideSuggestions: true, highlightedIndex: -1 });
        }
        break;
      default:
        break;
    }
  };

  private selectSuggestion(text: string): void {
    this.onChange(null, text);
    this.setState({
      value: this.transformValue(text),
      hideSuggestions: true,
      highlightedIndex: -1
    });
  }

  private renderSuggestionItems(suggestions: string[]): React.ReactNode {
    return suggestions.map((text, i) => {
      const onClick = () => {
        this.selectSuggestion(text);
      };
      const isHighlighted = i === this.state.highlightedIndex;

      return (
        <ActionButton
          key={i}
          id={`${this.listboxId}-option-${i}`}
          role="option"
          aria-selected={isHighlighted}
          style={{
            width: "100%",
            display: "block",
            backgroundColor: isHighlighted ? "var(--neutralLighter, #f3f2f1)" : undefined
          }}
          onClick={onClick}
        >
          {text}
        </ActionButton>
      );
    });
  }
}
